"use client";

import * as React from "react";
import { Input } from "../../input.js";
import { Button } from "../../button.js";
import { Plus } from "lucide-react";

export interface AttendeeSelectorProps {
  currentEmails: string[];
  onChange: (emails: string[]) => void;
  disabled?: boolean;
  placeholder?: string;
  // Optional external selector (e.g., Hub ClientEmailSelector) to render instead of the default input
  externalSelector?: (args: {
    currentEmails: string[];
    setEmails: (emails: string[]) => void;
    disabled: boolean;
  }) => React.ReactNode;
}

export function AttendeeSelector({
  currentEmails,
  onChange,
  disabled = false,
  placeholder = "Enter email addresses (comma-separated or press Enter)",
  externalSelector,
}: AttendeeSelectorProps) {
  const [value, setValue] = React.useState("");

  const addFromInput = React.useCallback(() => {
    const text = value.trim();
    if (!text) return;
    const parts = text
      .split(",")
      .map((e) => e.trim())
      .filter(Boolean);
    if (parts.length === 0) return;
    const next = Array.from(new Set([...currentEmails, ...parts]));
    onChange(next);
    setValue("");
  }, [value, currentEmails, onChange]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addFromInput();
    }
  };

  if (externalSelector) {
    return (
      <div 
        className="space-y-2"
        onClick={(e) => {
          // Prevent clicks inside the external selector from bubbling to form
          e.stopPropagation();
        }}
        onKeyDown={(e) => {
          // Prevent Enter key from submitting form when focused on selector
          if (e.key === "Enter") {
            e.stopPropagation();
          }
        }}
      >
        {externalSelector({ currentEmails, setEmails: onChange, disabled })}
      </div>
    );
  }

  return (
    <div className="flex gap-2">
      <Input
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        className="flex-1 border-gray-300 focus:border-blue-500 focus:ring-blue-500 hover:border-gray-400 transition-colors"
      />
      <Button
        type="button"
        onClick={addFromInput}
        disabled={disabled || !value.trim()}
        className="bg-blue-600 hover:bg-blue-700 text-white"
      >
        <Plus className="h-4 w-4" />
      </Button>
    </div>
  );
}

export default AttendeeSelector;


